import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ChevronLeft, ChevronRight, X, SkipForward, Zap, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface TourTooltipProps {
  title: string;
  description: string;
  actionText: string;
  emphasize?: boolean;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
  onClose: () => void;
  canGoBack: boolean;
  canGoForward: boolean;
  isLastStep: boolean;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  style?: React.CSSProperties;
}

export function TourTooltip({
  title,
  description,
  actionText,
  emphasize = false,
  currentStep,
  totalSteps,
  onNext,
  onPrevious,
  onSkip,
  onClose,
  canGoBack,
  canGoForward,
  isLastStep,
  position = 'bottom',
  className,
  style,
}: TourTooltipProps) {
  const { t } = useTranslation();

  const progress = ((currentStep + 1) / totalSteps) * 100;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: position === 'top' ? 20 : -20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8, y: position === 'top' ? 20 : -20 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className={cn('fixed z-50 max-w-md', className)}
        style={style}
      >
        <Card
          className={cn(
            'shadow-lg border-2',
            emphasize ? 'border-primary ring-2 ring-primary/20' : 'border-border'
          )}
        >
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {emphasize && <AlertCircle className="size-5 text-primary" />}
                <CardTitle className="text-lg font-semibold">{title}</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  {t('common.progress.stepOf', 'Step {{current}} of {{total}}', {
                    current: currentStep + 1,
                    total: totalSteps,
                  })}
                </Badge>
                <Button variant="ghost" size="sm" onClick={onClose} className="size-6 p-0">
                  <X className="size-4" />
                </Button>
              </div>
            </div>

            <Progress value={progress} className="h-2" />
          </CardHeader>

          <CardContent className="pt-0">
            <p className="mb-4 text-base leading-relaxed text-muted-foreground">{description}</p>

            {emphasize && (
              <div className="mb-4 rounded-lg border border-primary/20 bg-primary/10 p-3">
                <div className="flex items-center gap-2 text-primary">
                  <Zap className="size-4" />
                  <span className="text-sm font-medium">{t('common.important', 'Important!')}</span>
                </div>
              </div>
            )}

            <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onPrevious}
                  disabled={!canGoBack}
                  className="flex items-center gap-1"
                >
                  <ChevronLeft className="size-4" />
                  {t('common.buttons.previous', 'Previous')}
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onSkip}
                  className="flex items-center gap-1 text-muted-foreground"
                >
                  <SkipForward className="size-4" />
                  {t('common.buttons.skip', 'Skip Tour')}
                </Button>
              </div>

              <Button
                onClick={onNext}
                disabled={!canGoForward}
                className="flex h-auto items-center gap-1 whitespace-normal break-words py-3"
                variant={emphasize ? 'primary' : 'primary'}
              >
                {isLastStep ? (
                  t('common.buttons.finish', 'Finish')
                ) : (
                  <>
                    {actionText}
                    <ChevronRight className="size-4" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
}
